import {
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  message,
  Modal,
  Popconfirm,
  Select,
} from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { ExclamationCircleFilled } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { DEF_MONTH_FORMAT, GlobalUtils } from "@/app/home/utils/global.utils";
import { AddressRepo } from "@/core/repos/address.repo";
import { Ward } from "@/core/models/address/ward.model";
import { selectWardOptions } from "@/core/utils/ward-options";
import { wardLabel } from "@/app/home/components/address/useProvinceWards";
import { addressSearchProps } from "@/core/utils/address-search";

const waterIndexRepo = container.get(WaterIndexRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const addressRepo = container.get(AddressRepo);

export interface ExportInvoiceMonthQuery {
  month: string;
  year: string;
  tollAreaId?: string;
}

export interface _ExportInvoiceMonthQuery {
  date: Dayjs;
  wardId?: string;
  tollAreaId?: string;
}

interface ExportInvoiceMonthModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  data: ExportInvoiceMonthQuery;
}

const INPUT_WIDTH = 450;

export const ExportInvoiceMonthModal: React.FC<
  ExportInvoiceMonthModalProps
> = ({ isModalOpen, setIsModalOpen, data }) => {
  const [form] = Form.useForm<_ExportInvoiceMonthQuery>();
  const [loading, setLoading] = useState<boolean>(false);
  const [tollAreas, setTollAreas] = useState<TollAreaModel[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const selectedWardId = Form.useWatch("wardId", form);
  const selectedAreaId = Form.useWatch("tollAreaId", form);
  const defaultFilter = GlobalUtils.getPreviousMonthFilter();
  const [month, setMonth] = useState<string>(defaultFilter.month.value);
  const [year, setYear] = useState<string>(defaultFilter.year.value);
  const visibleAreas = selectedWardId
    ? tollAreas.filter((area) => area.wardId === selectedWardId)
    : tollAreas;
  const selectedWardName = wards.find((ward) => ward.id === selectedWardId)?.name;
  const selectedAreaName = tollAreas.find((area) => area.id === selectedAreaId)?.name;
  const scopeText = selectedAreaName && selectedWardName
    ? `khu vực ${selectedAreaName} thuộc xã ${selectedWardName}`
    : selectedAreaName
      ? `khu vực ${selectedAreaName}`
      : selectedWardName
        ? `xã ${selectedWardName}`
        : "tất cả khu vực";

  useEffect(() => {
    const date = dayjs(new Date(parseInt(data.year), parseInt(data.month) - 1));
    const area = tollAreas.find((item) => item.id === data.tollAreaId);
    setMonth(data.month);
    setYear(data.year);
    form.setFieldsValue({
      date,
      tollAreaId: data.tollAreaId,
      wardId: area?.wardId,
    });
  }, [data.month, data.year, data.tollAreaId, tollAreas, form]);

  useEffect(() => {
    const loadChoices = async () => {
      const [areas, factoryWards] = await Promise.all([
        tollAreaRepo.getAll(),
        addressRepo.getFactoryWards(),
      ]);
      setTollAreas(areas);
      setWards(selectWardOptions(factoryWards, areas.map((area) => area.wardId).filter(Boolean)));
    };
    loadChoices();
  }, []);

  const parseDateToMonthYear = (date: Dayjs) => {
    const month = (date.month() + 1).toString();
    const year = date.year().toString();
    return { month, year };
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const formData = await form.validateFields();
      message.info({
        content: `Đang tạo bảng tính phí tháng ${month}/${year} cho ${scopeText}. Vui lòng chờ xử lý xong`,
      });
      const result = await waterIndexRepo.exportInvoiceMonth(
        month,
        year,
        formData.tollAreaId,
        formData.wardId
      );
      if (result?.code === "00") {
        message.success({
          content: `Tạo bảng tính phí thành công`,
        });
      } else {
        message.error({
          content: `${result?.errorMessage}`,
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      message.error({
        content: `Tạo bảng tính phí tháng ${month}/${year} thất bại`,
      });
      console.log(error);
    }
    setLoading(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <ConfigProvider theme={MODAL_THEME}>
      <Modal
        open={isModalOpen}
        onCancel={handleCancel}
        width={510}
        centered
        styles={{
          mask: {
            backdropFilter: "blur(2px)",
            backgroundColor: "var(--primary-mask)",
          },
        }}
        footer={[
          <div key={1} className="w-full flex flex-row justify-end px-1.5">
            <Popconfirm
              title={`Tạo bảng tính phí?`}
              description={`Bạn có chắc chắn muốn tạo bảng tính phí tháng ${month}/${year} cho ${scopeText}?`}
              onConfirm={handleSave}
              okText="Tạo bảng tính phí"
              cancelText="Hủy"
              okButtonProps={{
                style: { backgroundColor: "var(--primary-color)" },
              }}
              icon={
                <ExclamationCircleFilled
                  style={{ color: "var(--danger-color)" }}
                />
              }
            >
              <Button
                key="submit"
                type="primary"
                loading={loading}
                style={{
                  background: "var(--primary-color)",
                  paddingRight: 48,
                  paddingLeft: 48,
                  fontSize: 18,
                  fontWeight: 600,
                  paddingTop: 18,
                  paddingBottom: 18,
                }}
              >
                TẠO BẢNG TÍNH PHÍ
              </Button>
            </Popconfirm>
          </div>,
        ]}
      >
        <div className="flex flex-row w-full items-start justify-start select-none">
          <p className="ml-2 text-2xl font-bold text-primary">
                TẠO BẢNG TÍNH PHÍ
          </p>
        </div>
        <div className="flex flex-row justify-center mt-6">
          <Form<_ExportInvoiceMonthQuery>
            name="basic"
            className="flex flex-col items-end"
            form={form}
            layout="vertical"
          >
            <Form.Item<_ExportInvoiceMonthQuery>
              label="Tháng"
              name="date"
              getValueProps={(i) => ({ value: dayjsPipe(i) })}
            >
              <DatePicker.MonthPicker
                format={DEF_MONTH_FORMAT}
                onChange={(date) => {
                  const { month, year } = parseDateToMonthYear(date);
                  setMonth(month);
                  setYear(year);
                }}
                style={{ width: INPUT_WIDTH }}
              />
            </Form.Item>
            <Form.Item<_ExportInvoiceMonthQuery>
              label="Xã"
              name="wardId"
            >
              <Select
                {...addressSearchProps}
                allowClear
                placeholder="Tất cả xã"
                options={wards.map((ward) => ({
                  value: ward.id,
                  label: wardLabel(ward),
                }))}
                onChange={(wardId?: string) => {
                  const areaId = form.getFieldValue("tollAreaId");
                  const area = tollAreas.find((item) => item.id === areaId);
                  if (area && wardId && area.wardId !== wardId) {
                    form.setFieldValue("tollAreaId", undefined);
                  }
                }}
                style={{ width: INPUT_WIDTH }}
              />
            </Form.Item>
            <Form.Item<_ExportInvoiceMonthQuery>
              label="Khu vực"
              name="tollAreaId"
            >
              <Select
                allowClear
                showSearch
                placeholder={selectedWardId ? "Tất cả khu vực trong xã" : "Tất cả khu vực"}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={visibleAreas.map((tollArea) => ({
                  value: tollArea.id,
                  label: tollArea.name,
                }))}
                onChange={(tollAreaId?: string) => {
                  const area = tollAreas.find((item) => item.id === tollAreaId);
                  if (area?.wardId) {
                    form.setFieldValue("wardId", area.wardId);
                  }
                }}
                style={{ width: INPUT_WIDTH }}
              />
            </Form.Item>
          </Form>
        </div>
        <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
      </Modal>
    </ConfigProvider>
  );
};
